/**
 * Geocoding Service — replaces reverseGeocode.js.
 * Uses the Google Maps Geocoding API (client-side) to convert
 * GPS coordinates into structured address components.
 *
 * Cost note: each call = 1 Geocoding API request (~$0.005).
 * We only call on: "Use current location" tap, and pin dragend.
 */

import { loadGoogleMaps } from './googleMapsLoader';

/**
 * Parse a Google Maps geocoding result's address_components into
 * the fields we need for the address form.
 *
 * @param {google.maps.GeocoderResult} result
 * @returns {{ line1: string, city: string, pincode: string, location: string }}
 */
export const parseAddressComponents = (result) => {
  const components = result.address_components || [];

  const get = (...types) => {
    for (const type of types) {
      const comp = components.find((c) => c.types.includes(type));
      if (comp) return comp.long_name;
    }
    return '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const line1 = [streetNumber, route].filter(Boolean).join(' ') || get('premise') || '';

  const city =
    get('locality') ||
    get('administrative_area_level_2') ||
    get('administrative_area_level_1') ||
    '';

  const pincode = get('postal_code') || '';

  const location =
    get('sublocality_level_1') ||
    get('sublocality') ||
    get('neighborhood') ||
    get('locality') ||
    '';

  return { line1, city, pincode, location };
};

/**
 * Reverse-geocode a lat/lng pair using the Google Maps Geocoding API.
 * Returns parsed address fields plus the raw first result.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<{ line1: string, city: string, pincode: string, location: string, formattedAddress: string }>}
 */
export const reverseGeocode = async (latitude, longitude) => {
  await loadGoogleMaps();

  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: latitude, lng: longitude } },
      (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const parsed = parseAddressComponents(results[0]);
          resolve({
            ...parsed,
            formattedAddress: results[0].formatted_address || '',
          });
        } else {
          console.error('[Geocoding] Reverse geocode failed:', status);
          reject(new Error(`Geocoding failed: ${status}`));
        }
      }
    );
  });
};
